import { createI18n, useI18n } from 'vue-i18n'
import { usePreferredLanguages, useLocalStorage } from '@vueuse/core'
/**
 * localStorage explicit locale preference
 * @type {string}
 */
const explicitlySpecifiedLanguage = useLocalStorage('preferred-language', null)

/**
 * @typedef {{name: string, load: () => Promise<{default:{}}> }} Translation
 *
 * @typedef {Object} Translations
 * @property {Translation} en
 */

/** @type {Translations} */
let locales
/**
 * local reference to i18n instance
 * @type {import('node_modules/vue-i18n/dist/vue-i18n').I18n}
 */
let i18nInstance
/**
 * Given
 * 1. explicit localStorage preference
 * 2. user's browser preferred language
 * (in order of importance)
 *
 * returns a locale code which can be used in this app.
 *
 * @returns {string} the user's preferred locale code implemented in this app
 */
export const getPreferredLanguage = () => {
  // and array of locale preferences like ['it-IT', 'it', 'en-EN', 'en']
  // in order of preference
  let preferences

  if (!explicitlySpecifiedLanguage.value) {
    // no explictly set locale: look at the browser preferences
    preferences = usePreferredLanguages().value
  } else {
    // explictly set locale, follow that
    preferences = [explicitlySpecifiedLanguage.value]
  }
  // loop preferences and find one that is implemented in this app
  const match = preferences.find(code => Object.keys(locales).indexOf(code) !== -1)
  // if preferred locale is not available in the app
  // default to en
  return match || 'en'
}
/**
 *
 * @param {Translations} translations
 */
export const setTranslations = (translations) => {
  locales = translations
}
/**
 *
 * @returns {Translations}
 */
export const getTranslations = () => {
  return locales
}

/**
 * Initializes i18n and creates the default i18n instance
 *
 * @returns {import('node_modules/vue-i18n/dist/vue-i18n').I18n} i18n instance
 */
export const setupI18n = () => {
  const locale = getPreferredLanguage()

  i18nInstance = createI18n({
    legacy: false,
    globalInjection: true,
    locale, // set locale
    fallbackLocale: 'en' // set fallback locale
  })

  return i18nInstance
}
/**
 * Loads locale messages for a given code
 *
 * @param {string} localeCode
 */
export const loadLocaleMessages = async (localeCode) => {
  if (!i18nInstance) throw new Error('i18n not inizialized yet. Call setupI18n first.')
  /** @type {Translation}  */
  const locale = locales[localeCode]

  if (!locale) {
    throw new Error('Locale not found')
  }

  const localeIsNotLoaded =
    // key not present
    !i18nInstance.global.messages.value[localeCode] ||
    // key present but no messages inside (created by default by vue-i18n)
    (i18nInstance.global.messages.value[localeCode] && Object.keys(i18nInstance.global.messages.value[localeCode]).length === 0)

  if (localeIsNotLoaded) {
    // console.log('Loading locale')
    // load locale messages with dynamic import
    const messages = await locale.load()
    // set locale and locale message
    i18nInstance.global.setLocaleMessage(localeCode, messages.default)
    // console.log('Locale Loaded')
  }
}
/**
 * Augments existing, not internationalized, routes
 * by adding /:locale/path param
 *
 * Also adds various redirects to enable, for example
 * * redirection from / to /en
 * * redirection from /path to /en/path
 * etc
 *
 * @param {import('node_modules/vue-router/dist/vue-router').RouteRecord[]} routes existing, not internationalized routes
 * @returns {import('node_modules/vue-router/dist/vue-router').RouteRecord[]} a new set of modified routes
 */
export const useLocalizedRoutes = (routes) => {
  let localizedRoutes = []

  const languagesRegex = Object.keys(locales).join('|')
  const preferredLanguage = getPreferredLanguage()

  let existingCatchAllRoute

  localizedRoutes = routes.map(route => {
    if (route.meta?.is404) {
      // remove 404 (catchall) route if present
      // we will modify it and re-add it at the end
      existingCatchAllRoute = route
      return null
    }
    // add /:locale(en|it|etc) to normal routes
    return {
      ...route,
      path: `/:locale(${languagesRegex})${route.path}`
    }
  }).filter(r => !!r) // erase null routes

  // append utility routes for redirection
  localizedRoutes = localizedRoutes.concat([
    {
      // matches /validlanguage/invalidroute and redirects it to /validlanguage
      path: `/:locale(${languagesRegex})/:pathMatch(.*)*`,
      redirect: to => {
        return { path: `/${preferredLanguage}` }
      }
    },
    {
      // matches /invalidlanguage(2 chars)/anyroute and redirects it to /validlanguage/anyroute
      path: '/:locale(\\w{2,2}\\b)/:pathMatch(.*)*',
      redirect: to => {
        return { path: `/${preferredLanguage}/${to.params.pathMatch.join('/')}` }
      }
    }
  ])

  // handle 404 (catchall) route at the end
  if (existingCatchAllRoute) {
    existingCatchAllRoute.redirect = to => {
      return { path: `/${preferredLanguage}/${Array.isArray(to.params.pathMatch) ? to.params.pathMatch.join('/') : to.params.pathMatch}` }
    }
    localizedRoutes.push(existingCatchAllRoute)
  } else {
    localizedRoutes.push({
      // matches /anyroute (if not found) and redirects it to /validlanguage/anyroute
      // https://next.router.vuejs.org/guide/migration/#removed-star-or-catch-all-routes
      path: '/:pathMatch(.*)*',
      redirect: to => {
        return { path: `/${preferredLanguage}/${Array.isArray(to.params.pathMatch) ? to.params.pathMatch.join('/') : to.params.pathMatch}` }
      }
    })
  }

  return localizedRoutes
}

/**
 * Adds a router guard that loads the current locale
 * if it's not already loaded
 *
 * @param {import('node_modules/vue-router/dist/vue-router').Router} router Router instance where to add the hooks
 */
export const addRouterHooks = (router) => {
  router.beforeEach(async (to, from, next) => {
    // there was a non null locale in the previous route
    // and now there's a different locale in this route
    // this means the locale was intentionally changed
    if (!!from.params?.locale && from.params?.locale !== to.params?.locale) {
      // this means we can set a localstorage value to
      // remember the user's preference
      explicitlySpecifiedLanguage.value = to.params.locale
      // console.log('changed locales intentionally')
    }

    if (to.params?.locale) {
      await loadLocaleMessages(to.params.locale)
      i18nInstance.global.locale.value = to.params.locale
    }
    next()
  })
}

export const i18n = useI18n
